import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { parseUrlFragment } from '../../core/utils/url-fragment';

/**
 * Receives the Toegang silent-SSO redirect inside a hidden iframe and
 * forwards the OIDC response (id_token + nonce, or error) to the parent
 * window via postMessage. The component renders nothing — its only job is
 * to bridge the iframe and the parent SPA.
 *
 * The parent must verify `event.origin === window.location.origin` before
 * trusting the payload (the iframe runs on our own origin, so this is a
 * meaningful guard against rogue postMessages).
 */
@Component({
  selector: 'onegate-silent-callback',
  template: '',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SilentCallbackComponent implements OnInit {
  static readonly MESSAGE_TYPE = 'onegate.toegang-sso';

  // eslint-disable-next-line class-methods-use-this
  ngOnInit(): void {
    SilentCallbackComponent.forwardToParent();
  }

  static forwardToParent(): void {
    if (window.parent === window.self) {
      // Loaded as a top-level navigation, not inside the SSO iframe.
      // Nothing useful to do — bail out silently.
      return;
    }

    const params = parseUrlFragment(
      window.location.hash || window.location.search,
    );

    window.parent.postMessage(
      {
        type: SilentCallbackComponent.MESSAGE_TYPE,
        idToken: params.get('id_token') ?? null,
        nonce: params.get('nonce') ?? null,
        state: params.get('state') ?? null,
        error: params.get('error') ?? null,
      },
      window.location.origin,
    );
  }
}
